/**
 * How the note model's add-ons are set beyond which are on: the layers the Layers add-on is
 * told, how much more than the score the More add-on is asked for, how far the add-ons are put
 * on, and the presets that set all of it at once.
 *
 * The Layers add-on was fitted with each thing it reads now told and now withheld, so any of
 * them can be left out here and the model decides that one for itself: the harmony, the rhythm,
 * each player's share, that share's own rhythm, and where a share says an earlier bar again.
 * The More add-on is told the score as Together is and asked for an amount besides
 * (`ARRANGE_MORE`): how many more moments each player strikes at than its share of the score.
 * Strength is how far whatever fitted add-on is on is put on, from the frozen model alone at 0
 * to the add-on in full at 100; a held add-on has none, so with no fitted one on there is no
 * strength to set. A preset (`ARRANGE_PRESETS`) is a setting of those switches and nothing
 * more: moving any of them afterwards leaves the preset, and taking one leaves a held add-on
 * as it stands (`presetOn`).
 */

import {
  ARRANGE_LAYERS,
  ARRANGE_MORE,
  ARRANGE_PRESET_NAMES,
  fittedOf,
  followPreset,
  presetOn,
  type ArrangeFollow,
  type ArrangeLayer,
  type ArrangeMore,
  type ArrangePreset,
} from "@app/shared-types";
import { LABEL } from "./Chrome";

/** Each layer as its switch names it. Keyed by layer so a new one cannot go unnamed. */
export const LAYER_NAMES: Readonly<
  Record<ArrangeLayer, { label: string; of: string; title: string }>
> = {
  harmony: {
    label: "Harmony",
    of: "the harmony",
    title:
      "Tell the model what the score's accompaniment sounds under the tune, quarter by quarter, and which note is lowest. Left out, the model chooses its own chords.",
  },
  rhythm: {
    label: "Rhythm",
    of: "the rhythm",
    title:
      "Tell the model when the score's accompaniment strikes a note and how busy each bar is. Left out, the model chooses its own rhythm.",
  },
  roles: {
    label: "Shares",
    of: "each player's share",
    title:
      "Tell each player how much of the bar's accompaniment is its own: the lowest notes the lowest part's, the middle the middle part's, the highest the highest's, of the three the model writes under the tune. Left out, each player chooses how much to play, and may play more than the score gives it.",
  },
  part: {
    label: "Own rhythm",
    of: "each player's own rhythm",
    title:
      "Tell each player when its own share of the accompaniment strikes. Left out, a player keeps the bar's rhythm but places its own notes in it.",
  },
  again: {
    label: "Repeats",
    of: "where a bar comes again",
    title:
      "Tell each player where its share says again what it said one, two or four bars before: the same moments, the same shape, or the very same notes. Left out, the model decides when to keep a figure and when to leave it.",
  },
};

/** Each amount of more as its menu names it. Keyed by amount so a new one cannot go unnamed. */
export const MORE_NAMES: Readonly<Record<ArrangeMore, { label: string; of: string }>> = {
  1: { label: "A little", of: "up to half as many notes again as the score gives it" },
  2: { label: "More", of: "up to twice the notes the score gives it" },
  3: { label: "Much more", of: "up to three times the notes the score gives it" },
  4: { label: "Most", of: "the most it writes: about three times the notes the score gives it" },
};

const PRESET_NAMES: Readonly<Record<ArrangePreset, { label: string; title: string }>> = {
  keep: {
    label: "Keep",
    title:
      "Stay closest to the score: the Together add-on, in full. The players keep the score's harmony, its rhythm and their own shares of it, in notes of the model's own.",
  },
  add: {
    label: "Add",
    title:
      "Keep the score's harmony and strike where the score strikes, as Keep does, and have each player write more notes than the score gives it: the More add-on, in full, asked for up to twice as many.",
  },
  loosen: {
    label: "Loosen",
    title:
      "Keep the score's harmony, rhythm and repeats, but leave each player free of its share: the players divide the bar among themselves, and play together more often than the score's own parts do.",
  },
  change: {
    label: "Change",
    title:
      "Keep the score's harmony and nothing else: the rhythm and the figures under the tune are the model's own.",
  },
};

const BOX =
  "inline-flex h-7 cursor-pointer items-center gap-1.5 text-[11px] text-shell-text hover:text-shell-lit";

/** The presets, each a button lit while the switches stand as it sets them. */
export function ArrangePresets({
  follow,
  onFollow,
}: {
  readonly follow: ArrangeFollow;
  onFollow(follow: ArrangeFollow): void;
}): JSX.Element {
  const preset = followPreset(follow);
  return (
    <>
      {ARRANGE_PRESET_NAMES.map((name) => (
        <button
          key={name}
          type="button"
          title={PRESET_NAMES[name].title}
          aria-pressed={preset === name}
          onClick={() => onFollow(presetOn(follow, name))}
          className={`h-7 rounded px-2 text-[11px] ${preset === name ? "bg-shell-hot text-shell-lit" : "text-shell-text hover:bg-shell-key hover:text-shell-lit"}`}
        >
          {PRESET_NAMES[name].label}
        </button>
      ))}
    </>
  );
}

export default function ArrangeLayers({
  follow,
  onFollow,
}: {
  readonly follow: ArrangeFollow;
  onFollow(follow: ArrangeFollow): void;
}): JSX.Element {
  const percent = Math.round(follow.strength * 100);
  return (
    <>
      {follow.hints.includes("layers") ? (
        <>
          <span className={LABEL}>Told</span>
          {ARRANGE_LAYERS.map((layer) => (
            <label key={layer} title={LAYER_NAMES[layer].title} className={BOX}>
              <input
                type="checkbox"
                checked={follow.layers.includes(layer)}
                onChange={(event) =>
                  onFollow({
                    ...follow,
                    layers: ARRANGE_LAYERS.filter((one) =>
                      one === layer ? event.target.checked : follow.layers.includes(one),
                    ),
                  })
                }
                className="h-3.5 w-3.5 accent-shell-live"
              />
              {LAYER_NAMES[layer].label}
            </label>
          ))}
        </>
      ) : null}
      {follow.hints.includes("more") ? (
        <label
          title="How much more than the score each player is asked to write, counted in the moments it strikes at against its own share of the score's bar. A player the score gives nothing in a bar still rests."
          className="inline-flex h-7 items-center gap-1.5 text-[11px] text-shell-text"
        >
          <span className={LABEL}>How much</span>
          <select
            value={follow.more}
            onChange={(event) =>
              onFollow({
                ...follow,
                more: ARRANGE_MORE.find((one) => one === Number(event.target.value)) ?? follow.more,
              })
            }
            className="h-6 rounded bg-shell-key px-1 text-[11px] text-shell-lit"
          >
            {ARRANGE_MORE.map((more) => (
              <option key={more} value={more} title={MORE_NAMES[more].of}>
                {MORE_NAMES[more].label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {fittedOf(follow.hints).length === 0 ? null : (
        <label
          title="How far the fitted add-ons are put on: 0 is the note model alone, shown only the tune; 100 is the add-ons in full. In between, the parts follow the score less closely and the model decides more for itself."
          className="inline-flex h-7 items-center gap-1.5 text-[11px] text-shell-text"
        >
          <span className={LABEL}>Strength</span>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={percent}
            onChange={(event) =>
              onFollow({ ...follow, strength: Number(event.target.value) / 100 })
            }
            className="w-20 accent-shell-live"
          />
          <span className="w-8 font-mono text-[10px] text-shell-dim">{percent}%</span>
        </label>
      )}
    </>
  );
}
